// Import necessary components and types
import { AlertCircle } from 'lucide-react'; // Icon for empty state
import { JobCard } from '../job/JobCard'; // Component to display individual job cards
import type { Job } from '../../types/job'; // Type definition for jobs

// Define the props for the JobsSection component
interface JobsSectionProps {
  jobs: Job[]; // List of jobs to display
  onToggleStatus: (id: number, currentStatus: boolean) => void; // Callback to toggle job status
  onDelete: (id: number) => void; // Callback to delete a job
}

/**
 * Component to display a section of jobs assigned to the user.
 * Includes actions for toggling status and deleting jobs.
 */
export function JobsSection({ jobs, onToggleStatus, onDelete }: JobsSectionProps) {
  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      {/* Header Section */}
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Vos offres d'emploi assignées {/* Section title */}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {jobs.length} offre{jobs.length !== 1 ? 's' : ''} sous votre responsabilité {/* Dynamic job count */}
        </p>
      </div>

      {/* Job Cards Section */}
      <div className="border-t border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
          {/* Render JobCard components for each job */}
          {jobs.map((job) => (
            <JobCard
              key={job.id} // Unique key for React list rendering
              job={job} // Pass the job object to the JobCard
              onToggleStatus={() => onToggleStatus(job.id, job.active)} // Trigger toggle status action
              onDelete={() => onDelete(job.id)} // Trigger delete action
              showActions // Prop to display action buttons
            />
          ))}

          {/* Empty State */}
          {jobs.length === 0 && (
            <div className="col-span-full text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" /> {/* Alert icon */}
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Aucune offre assignée
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Vous n'avez pas encore d'offres d'emploi sous votre responsabilité.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
